import { ParseMessage } from './ParseError';
import { Scanner } from './Scanner';
import { Token, type MatchRange, type NumberToken, type TokenType } from './Token';

export class Lexer extends Scanner {
  constructor(text: string, opts?: { lineIndex?: import('./LineIndex').LineIndex }) {
    super({ text, lineIndex: opts?.lineIndex });
  }

  token(token: Token | undefined, type: TokenType): boolean {
    if (token !== undefined) {
      const t = token.clone();
      t.type = type;
      this.tokens.push(t);
      return true;
    }
    return false;
  }

  lastTokenIsType(type: TokenType): boolean {
    return this.tokens.length > 0 && this.tokens[this.tokens.length - 1].type === type;
  }

  comment(token: Token): void {
    this.comments.push(token);
  }

  // Check if we've reached the end of the input.
  get isEOF(): boolean {
    return this.pos >= this.text.length;
  }

  // Generic method to match a regex anchored at the beginning of the remaining input.
  // Returns a Token with the matched text and its range if found, otherwise undefined.
  match(regex: RegExp): Token | undefined {
    const remaining = this.text.slice(this.pos);
    if (!regex.source.startsWith('^')) {
      throw new Error(`Regex must be anchored at the beginning: ${regex.source}`);
    }
    const m = remaining.match(regex);
    if (m) {
      return new Token(m[0], { start: this.pos, end: this.pos + m[0].length });
    }
    return undefined;
  }

  // Consume a number of characters from the input.
  // Can accept either an object with a 'length' property or a bare number.
  consume(m: { length: number } | number): void {
    const length = typeof m === 'number' ? m : m.length;
    this.pos += length;
  }

  consumeMatch(regex: RegExp): Token | undefined {
    const m = this.match(regex);
    if (m !== undefined && m.length > 0) {
      this.consume(m);
      return m;
    } else {
      return undefined;
    }
  }

  // Attempts to parse a specific literal string from the current position.
  // If successful, consumes it and returns a Token; otherwise, returns undefined.
  stringLiteral(literal: string): Token | undefined {
    if (this.text.startsWith(literal, this.pos)) {
      const start = this.pos;
      this.consume(literal.length);
      return new Token(literal, { start, end: this.pos });
    } else {
      return undefined;
    }
  }

  expectStringLiteral(literal: string): Token {
    const result = this.stringLiteral(literal);
    if (result === undefined) {
      throw this.error(`Expected literal '${literal}'`, this.pos);
    }
    return result;
  }

  // Attempts to parse an integer at the current position.
  // If successful, consumes it and returns an IntegerMatch (with the parsed number and range).
  integer(): NumberToken | ParseMessage {
    const m = this.match(/^0|[1-9]\d*/);
    if (m) {
      const value = parseInt(m.text);
      this.consume(m);
      return new Token(m.text, { start: m.start, end: m.end, value });
    } else {
      return this.error('Expected an integer', this.pos);
    }
  }

  expectInteger(): NumberToken {
    const result = this.integer();
    if (result instanceof ParseMessage) {
      throw result;
    }
    return result;
  }

  skipWS(): boolean {
    // Whitespace is space and tab characters, as well as escaped newlines
    // (line-continuation characters).
    return this.consumeMatch(/^([ \t]|(\\\r?\n))*/) ? true : false;
  }

  skipNL(): boolean {
    return this.consumeMatch(/^\r?\n/) ? true : false;
  }

  expectNL(): void {
    if (!this.skipNL()) {
      throw this.error('Expected newline', this.pos);
    }
  }

  consumeUntilNLOrEOF(): Token | undefined {
    const start = this.pos;
    const m = this.match(/^[^\r\n]*/);
    if (m) {
      // if nothing was matched and we are not at EOF, return undefined; but at EOF return an empty match
      if (m.text.length === 0 && !this.isEOF) {
        return undefined;
      }
      this.consume(m);
      return new Token(m.text, { start, end: this.pos });
    }
    return undefined;
  }

  // Takes a pair of balanced delimiters, e.g. ('{', '}') or ('/*', '*/'),
  // and consumes text until the closing delimiter is found, accounting
  // for nested delimiters.
  //
  // Returns a Token with the consumed text and its range if successful,
  // otherwise undefined, and the parser's position is unchanged.
  consumeUntilBalanced(open: string, close: string): Token | undefined {
    return this.withSavedPosition(commit => {
      const start = this.pos;
      if (!this.text.startsWith(open, this.pos)) {
        return undefined;
      }
      this.consume(open.length);
      let depth = 1;
      while (!this.isEOF) {
        if (this.text.startsWith(open, this.pos)) {
          this.consume(open.length);
          depth++;
        } else if (this.text.startsWith(close, this.pos)) {
          if (depth > 0) {
            this.consume(close.length);
            depth--;
            if (depth === 0) {
              const end = this.pos;
              commit();
              return new Token(this.text.slice(start, end), { start, end });
            }
          } else {
            return undefined;
          }
        } else {
          this.consume(1);
        }
      }
      return undefined;
    });
  }

  skipLineComment(): boolean {
    const match = this.match(/\/\/.*$/);
    if (match) {
      this.consume(match);
      return true;
    }
    return false;
  }

  expectNLOrEOF(): boolean {
    if (this.isEOF) {
      return false;
    }
    this.expectNL();
    return true;
  }

  skipAllWSAndNL(): boolean {
    let found = false;
    while (this.skipWS() || this.skipNL()) {
      found = true;
    }
    return found;
  }

  skipToNextToken(): boolean {
    return this.skipAllWSAndNL();
  }

  // A generic convenience method to parse a pattern.
  // Consumes the matched text and returns its match details if successful.
  pattern(regex: RegExp): Token | ParseMessage {
    const m = this.match(regex);
    if (m) {
      this.consume(m);
      return m;
    } else {
      return this.error(`Expected pattern ${regex}`, this.pos);
    }
  }
}

export function combine(tokens: (Token | undefined)[]): MatchRange {
  const m = tokens.filter(m => m !== undefined);
  const text = m.map(m => m.text).join('');
  const expectedLength = m.reduce((sum, m) => sum + m.length, 0);
  const matchRange = {
    text,
    start: m[0].start,
    end: m[m.length - 1].end
  };
  const length = matchRange.end - matchRange.start;
  if (length !== expectedLength) {
    throw new Error(
      `Combined token length ${length} does not match expected length ${expectedLength}`
    );
  }
  return matchRange;
}
